import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthService } from '../services/auth.service';
import { RoleCode } from '../../shared/models/user/user.enums';

/**
 * Route guard that checks if user has required role.
 * Does NOT check authentication - that's handled by authGuard.
 * When used as canMatch, returns true for unauthenticated users to let authGuard handle redirect.
 */
export const roleGuard: CanActivateFn = (route): boolean => {
	const router = inject(Router);
	const authService = inject(AuthService);

	if (!authService.getAccessToken()) {
		return true;
	}

	const allowedRoles = route.data['allowedRoles'] as RoleCode[] | undefined;
	if (!allowedRoles?.length) {
		return true;
	}

	if (authService.isRole(allowedRoles)) {
		return true;
	}

	if (hasRoleSwitchParam()) {
		sessionStorage.removeItem('lastUrl');
		void router.navigate(['/'], { state: { skipCanLeave: true } });
	} else {
		void router.navigate(['/error/403'], { state: { skipCanLeave: true } });
	}

	return false;
};

/**
 * Checks if URL contains role switch parameter (?rc=1).
 * This parameter is set by header component when user changes their active role.
 *
 * @returns {boolean} True if role switch parameter is present, false otherwise.
 * @throws {Error} If window.location.href is not a valid URL.
 */
function hasRoleSwitchParam(): boolean {
	return new URL(window.location.href).searchParams.get('rc') === '1';
}
